import type React from "react";
import { createContext, useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { settings } from "@/lib/utils/settings/settings";
import { decodeToken, generateToken } from "@/services/AuthService";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { HttpClient } from "@/services/HttpClient";
import { getAuthService } from "@/services/user-auth/auth-factory";
import type { AuthService } from "@/services/user-auth/auth-service";
import type { UserRole } from "@/services/user-auth/user-roles";
import { LocalStorageKeys, type User } from "@/shared/types/common";

export interface AuthContextType {
	isAuthenticated: boolean;
	accessToken: string | null;
	user: User | null;
	roles: UserRole[] | null;
	authService: AuthService | null;
	generateToken: (authCode: string) => Promise<void>;
	login: () => void;
	logout: () => void;
	updateUserRoles: (roles: UserRole[]) => void;
	getRolesFromToken: (token: string) => UserRole[];
}

const AUTH_PROVIDER = "cognito:groups" as const;

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [accessToken, setAccessToken] = useState<string | null>(
		HttpClient.getTokens()?.access_token || null,
	);
	const [user, setUser] = useLocalStorage<User | null>(
		LocalStorageKeys.user,
		null,
	);
	const [, setAttemptToBuy] = useLocalStorage<boolean>(
		LocalStorageKeys.attemptToBuy,
		false,
	);
	const [roles, setRoles] = useState<UserRole[] | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState(!!accessToken);
	const navigate = useNavigate();

	const generateTokenHandler = async (authCode: string) => {
		try {
			const tokens = await generateToken(authCode);
			setAccessToken(tokens.access_token);

			HttpClient.setAuthorization({
				access_token: tokens.access_token,
				refresh_token: tokens.refresh_token,
				expires_in: tokens.expires_in,
			});
			const userInfo = decodeToken(tokens.id_token);
			const userData = {
				email: userInfo.email,
			};
			setRoles((userInfo[AUTH_PROVIDER] || []) as UserRole[]);
			setIsAuthenticated(true);
			setUser(userData);

			navigate("/dashboard");
		} catch (error) {
			ErrorHandler.logError(error);
		}
	};

	const getRolesFromToken = (token: string) => {
		return (decodeToken(token)[AUTH_PROVIDER] || []) as UserRole[];
	};

	const login = useCallback(() => {
		const clientId = settings.oauthClientId;
		const redirectUri = `${window.location.origin}/auth`;
		const authUrl = `${settings.authUrl}/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
		window.location.href = authUrl;
	}, []);

	const logout = useCallback(() => {
		setUser(null);
		HttpClient.logout();
		sessionStorage.setItem(LocalStorageKeys.attemptToConfirmPhone, "0");
		sessionStorage.removeItem(LocalStorageKeys.phoneConfirm);

		setAttemptToBuy(false);
	}, [setUser, setAttemptToBuy]);

	const updateUserRoles = useCallback(
		(roles: UserRole[]) => {
			setRoles(user ? roles : null);
		},
		[user],
	);

	return (
		<AuthContext.Provider
			value={{
				isAuthenticated,
				accessToken,
				user,
				authService: getAuthService(roles),
				generateToken: generateTokenHandler,
				roles,
				login,
				logout,
				updateUserRoles,
				getRolesFromToken,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
